import {
    AreaChart,
    BarChart3,
    Calendar,
    ChevronDown,
    ChevronUp,
    Database,
    Hash,
    Info,
    LayoutDashboard,
    LineChart,
    Loader2,
    PieChart,
    Plus,
    Table as TableIcon,
    X,
} from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useState } from 'react';

import {
    ResponsiveModal,
    ResponsiveModalContent,
    ResponsiveModalDescription,
    ResponsiveModalFooter,
    ResponsiveModalTitle,
} from '@/components/ui/responsive-modal';
import { Button } from '@/components/ui/shadcn/button';
import { Input } from '@/components/ui/shadcn/input';
import { Label } from '@/components/ui/shadcn/label';
import { MultiSelect } from '@/components/ui/shadcn/multi-select';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/shadcn/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/shadcn/tooltip';
import { useTranslations } from '@/hooks/useTranslations';
import { cn } from '@/lib/utils';
import type { AnalyticsDateRange } from '@/types/analytics';
import type {
    Aggregation,
    ChartType,
    ComparisonAxis,
    ComparisonChartType,
    ComparisonSeries,
    ReportRegistry,
    ReportSource,
    WidgetConfig,
    WidgetDraft,
    WidgetSize,
} from '@/types/dashboards';
import { isComparisonWidget } from '@/types/dashboards';

import WidgetCardShell from './WidgetCardShell';
import WidgetChart from './WidgetChart';
import { useWidgetPreview } from './useWidgetPreview';

const NO_DATE = '__all_time__';

const CHART_ICONS: Record<ChartType, typeof BarChart3> = {
    bar: BarChart3,
    line: LineChart,
    area: AreaChart,
    pie: PieChart,
    kpi: Hash,
    table: TableIcon,
};

interface WidgetConfigSheetProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    registry: ReportRegistry;
    dateRange: AnalyticsDateRange;
    initialWidget?: WidgetConfig | null;
    onSave: (widget: WidgetConfig) => void;
}

function defaultSeries(source: ReportSource): ComparisonSeries {
    const measure = source.measures[0];
    return {
        source: source.key,
        measure: measure?.key ?? 'count',
        aggregation: measure?.aggregations[0] ?? 'count',
        date_field: source.date_fields[0]?.key ?? null,
        filters: [],
    };
}

export default function WidgetConfigSheet({ open, onOpenChange, registry, dateRange, initialWidget, onSave }: WidgetConfigSheetProps) {
    const { t } = useTranslations();
    const firstSource = registry.sources[0];

    // Shared
    const [id, setId] = useState('');
    const [title, setTitle] = useState('');
    const [size, setSize] = useState<WidgetSize>('medium');
    const [mode, setMode] = useState<'single' | 'comparison'>('single');
    const [filtersOpen, setFiltersOpen] = useState(true);

    // Single
    const [chartType, setChartType] = useState<ChartType>('bar');
    const [source, setSource] = useState<string>(firstSource?.key ?? '');
    const [dimension, setDimension] = useState<string | null>(firstSource?.dimensions[0]?.key ?? firstSource?.date_fields[0]?.key ?? null);
    const [measure, setMeasure] = useState<string>(firstSource?.measures[0]?.key ?? 'count');
    const [aggregation, setAggregation] = useState<Aggregation>(firstSource?.measures[0]?.aggregations[0] ?? 'count');
    const [dateField, setDateField] = useState<string | null>(firstSource?.date_fields[0]?.key ?? null);
    const [filters, setFilters] = useState<Array<{ dimension: string; values: string[] }>>([]);

    // Comparison
    const [compChartType, setCompChartType] = useState<ComparisonChartType>('line');
    const [axis, setAxis] = useState<ComparisonAxis>('time');
    const [series, setSeries] = useState<ComparisonSeries[]>(firstSource ? [defaultSeries(firstSource)] : []);

    useEffect(() => {
        if (!open) return;

        setId(initialWidget?.id ?? crypto.randomUUID());
        setTitle(initialWidget?.title ?? '');
        setSize(initialWidget?.size ?? 'medium');
        setFiltersOpen(true);

        if (initialWidget && isComparisonWidget(initialWidget)) {
            setMode('comparison');
            setCompChartType(initialWidget.chart_type);
            setAxis(initialWidget.axis);
            setSeries(initialWidget.series);
        } else if (initialWidget) {
            setMode('single');
            setChartType(initialWidget.chart_type);
            setSource(initialWidget.source);
            setDimension(initialWidget.dimension);
            setMeasure(initialWidget.measure);
            setAggregation(initialWidget.aggregation);
            setDateField(initialWidget.date_field);
            setFilters(initialWidget.filters);
        } else if (firstSource) {
            setMode('single');
            setChartType('bar');
            setSource(firstSource.key);
            setDimension(firstSource.dimensions[0]?.key ?? firstSource.date_fields[0]?.key ?? null);
            setMeasure(firstSource.measures[0]?.key ?? 'count');
            setAggregation(firstSource.measures[0]?.aggregations[0] ?? 'count');
            setDateField(firstSource.date_fields[0]?.key ?? null);
            setFilters([]);
            setCompChartType('line');
            setAxis('time');
            setSeries([defaultSeries(firstSource)]);
        }
    }, [open, initialWidget, firstSource]);

    const sourceConfig = useMemo(() => registry.sources.find((s) => s.key === source) ?? firstSource, [registry, source, firstSource]);
    const measureConfig = sourceConfig?.measures.find((m) => m.key === measure) ?? sourceConfig?.measures[0];

    const draft: WidgetDraft = useMemo(() => {
        if (mode === 'comparison') {
            return { id, title, size, mode, chart_type: compChartType, axis, series };
        }
        return {
            id,
            title,
            size,
            mode: 'single',
            chart_type: chartType,
            source,
            dimension: chartType === 'kpi' ? null : dimension,
            measure,
            aggregation,
            date_field: dateField,
            filters,
        };
    }, [mode, id, title, size, compChartType, axis, series, chartType, source, dimension, measure, aggregation, dateField, filters]);

    const preview = useWidgetPreview(draft, dateRange);

    const chartTypes: Array<{ value: ChartType; label: string }> = [
        { value: 'bar', label: t('dashboards.builder.chart_types.bar', 'Bar') },
        { value: 'line', label: t('dashboards.builder.chart_types.line', 'Line') },
        { value: 'area', label: t('dashboards.builder.chart_types.area', 'Area') },
        { value: 'pie', label: t('dashboards.builder.chart_types.pie', 'Pie / Donut') },
        { value: 'kpi', label: t('dashboards.builder.chart_types.kpi', 'KPI number') },
        { value: 'table', label: t('dashboards.builder.chart_types.table', 'Table') },
    ];
    const comparisonChartTypes = chartTypes.filter(
        (c) => c.value === 'bar' || c.value === 'line' || c.value === 'area' || c.value === 'table',
    ) as Array<{
        value: ComparisonChartType;
        label: string;
    }>;

    const aggregationLabel = (value: Aggregation): string =>
        ({
            count: t('dashboards.builder.aggregations.count', 'Count'),
            sum: t('dashboards.builder.aggregations.sum', 'Sum'),
            avg: t('dashboards.builder.aggregations.avg', 'Average'),
        })[value];

    const availableAxes: Array<{ value: ComparisonAxis; label: string }> = [
        { value: 'time', label: t('dashboards.builder.axes.time', 'Over time') },
        ...registry.shared_axes.map((sharedAxis) => ({ value: sharedAxis, label: t(`dashboards.builder.axes.${sharedAxis}`, sharedAxis) })),
    ];

    const sourcesForAxis = (forAxis: ComparisonAxis): ReportSource[] =>
        forAxis === 'time' ? registry.sources : registry.sources.filter((s) => s.dimensions.some((d) => d.shared === forAxis));

    const changeSingleSource = (key: string) => {
        const next = registry.sources.find((s) => s.key === key);
        if (!next) return;
        setSource(key);
        setMeasure(next.measures[0]?.key ?? 'count');
        setAggregation(next.measures[0]?.aggregations[0] ?? 'count');
        setDimension(chartType === 'kpi' ? null : (next.dimensions[0]?.key ?? next.date_fields[0]?.key ?? null));
        setDateField(next.date_fields[0]?.key ?? null);
        setFilters([]);
    };

    const changeSingleMeasure = (key: string) => {
        setMeasure(key);
        setAggregation(sourceConfig?.measures.find((m) => m.key === key)?.aggregations[0] ?? 'count');
    };

    const filterValues = (dimensionKey: string): string[] => filters.find((f) => f.dimension === dimensionKey)?.values ?? [];
    const setFilter = (dimensionKey: string, values: string[]) => {
        setFilters((prev) => {
            const others = prev.filter((f) => f.dimension !== dimensionKey);
            return values.length > 0 ? [...others, { dimension: dimensionKey, values }] : others;
        });
    };
    const filterableDimensions = (sourceConfig?.dimensions ?? []).filter(
        (dim) => (dim.type === 'enum' || dim.type === 'config' || dim.type === 'plain') && dim.options.length > 0,
    );

    const changeAxis = (value: ComparisonAxis) => {
        setAxis(value);
        const allowed = sourcesForAxis(value);
        setSeries((prev) =>
            prev.map((serie) => (allowed.some((s) => s.key === serie.source) ? serie : defaultSeries(allowed[0] ?? registry.sources[0]))),
        );
    };
    const updateSeries = (index: number, patch: Partial<ComparisonSeries>) =>
        setSeries((prev) => prev.map((serie, i) => (i === index ? { ...serie, ...patch } : serie)));
    const changeSeriesSource = (index: number, key: string) => {
        const next = registry.sources.find((s) => s.key === key);
        if (!next) return;
        updateSeries(index, {
            source: key,
            measure: next.measures[0]?.key ?? 'count',
            aggregation: next.measures[0]?.aggregations[0] ?? 'count',
            date_field: next.date_fields[0]?.key ?? null,
        });
    };
    const addSeries = () => {
        const allowed = sourcesForAxis(axis);
        if (allowed[0]) setSeries((prev) => [...prev, defaultSeries(allowed[0])]);
    };
    const removeSeries = (index: number) => setSeries((prev) => prev.filter((_, i) => i !== index));

    // Preview header/footer metadata
    const measureLabel = measureConfig?.label ?? '';
    const dimensionLabel =
        sourceConfig?.dimensions.find((dim) => dim.key === dimension)?.label ?? sourceConfig?.date_fields.find((f) => f.key === dimension)?.label;
    const autoTitle =
        mode === 'comparison'
            ? `${t('dashboards.builder.type_comparison', 'Comparison')} · ${availableAxes.find((a) => a.value === axis)?.label ?? ''}`
            : dimensionLabel
              ? t('dashboards.builder.auto_title', ':measure by :dimension').replace(':measure', measureLabel).replace(':dimension', dimensionLabel)
              : measureLabel;
    const previewTitle = title.trim() || autoTitle;
    const previewSubtitle =
        mode === 'comparison'
            ? `${series.length} ${t('dashboards.builder.series', 'Series').toLowerCase()}`
            : `${measureLabel} · ${sourceConfig?.label ?? ''}`;
    const dateFieldLabel = sourceConfig?.date_fields.find((f) => f.key === dateField)?.label ?? '';
    const previewFooter =
        mode === 'single'
            ? `${t('dashboards.builder.date_filter', 'Date filter')}: ${dateField ? `${dateFieldLabel} · ${dateRange.from_date} – ${dateRange.to_date}` : t('dashboards.builder.all_time', 'All time')}`
            : `${t('dashboards.builder.axis', 'Compare across')}: ${availableAxes.find((a) => a.value === axis)?.label ?? ''}`;

    const handleSave = () => {
        const widget: WidgetConfig =
            mode === 'comparison'
                ? { id, size, mode: 'comparison', chart_type: compChartType, axis, series, title: title.trim() || autoTitle }
                : {
                      id,
                      size,
                      mode: 'single',
                      chart_type: chartType,
                      source,
                      dimension: chartType === 'kpi' ? null : dimension,
                      measure,
                      aggregation,
                      date_field: dateField,
                      filters,
                      title: title.trim() || autoTitle,
                  };
        onSave(widget);
        onOpenChange(false);
    };

    const allowedSources = sourcesForAxis(axis);

    return (
        <ResponsiveModal open={open} onOpenChange={onOpenChange} variant="sheet">
            {/* The config panel already owns the scrolling middle. */}
            <ResponsiveModalContent size="xl">
                {/* Header */}
                <div className="flex items-start gap-3 p-6 pb-4">
                    <div className="bg-primary/10 text-primary flex h-10 w-10 shrink-0 items-center justify-center rounded-xl">
                        <LayoutDashboard className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                        <ResponsiveModalTitle className="text-lg">
                            {initialWidget ? t('dashboards.builder.edit_widget', 'Edit widget') : t('dashboards.builder.add_widget', 'Add widget')}
                        </ResponsiveModalTitle>
                        <ResponsiveModalDescription>
                            {t(
                                'dashboards.builder.dialog_subtitle',
                                'Choose your data, visualization, and filters. The preview updates in real time.',
                            )}
                        </ResponsiveModalDescription>
                    </div>
                </div>

                <TooltipProvider delayDuration={200}>
                    <div className="flex-1 space-y-4 overflow-y-auto px-6 pb-2">
                        {/* 1 · Basics */}
                        <SectionCard step={1} title={t('dashboards.builder.section_basics', 'Basics')}>
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                <div>
                                    <FieldLabel hint={t('dashboards.builder.hints.title', '')}>
                                        {t('dashboards.builder.title_field', 'Title')}
                                    </FieldLabel>
                                    <Input
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        placeholder={t('dashboards.builder.title_placeholder', 'Optional — auto-named if left blank')}
                                    />
                                </div>
                                <div>
                                    <FieldLabel hint={t('dashboards.builder.hints.widget_type', '')}>
                                        {t('dashboards.builder.widget_type', 'Widget type')}
                                    </FieldLabel>
                                    <Select value={mode} onValueChange={(v) => setMode(v as 'single' | 'comparison')}>
                                        <SelectTrigger className="w-full">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="single">{t('dashboards.builder.type_single', 'Single metric')}</SelectItem>
                                            <SelectItem value="comparison">
                                                {t('dashboards.builder.type_comparison', 'Comparison (cross-reference)')}
                                            </SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        </SectionCard>

                        {/* 2 · Visualization */}
                        <SectionCard step={2} title={t('dashboards.builder.section_visualization', 'Visualization')}>
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                <div>
                                    <FieldLabel hint={t('dashboards.builder.hints.chart_type', '')}>
                                        {t('dashboards.builder.chart_type', 'Chart type')}
                                    </FieldLabel>
                                    {mode === 'single' ? (
                                        <Select value={chartType} onValueChange={(v) => setChartType(v as ChartType)}>
                                            <SelectTrigger className="w-full">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {chartTypes.map((c) => (
                                                    <SelectItem key={c.value} value={c.value}>
                                                        <ChartTypeLabel type={c.value} label={c.label} />
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    ) : (
                                        <Select value={compChartType} onValueChange={(v) => setCompChartType(v as ComparisonChartType)}>
                                            <SelectTrigger className="w-full">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {comparisonChartTypes.map((c) => (
                                                    <SelectItem key={c.value} value={c.value}>
                                                        <ChartTypeLabel type={c.value} label={c.label} />
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    )}
                                </div>
                                <div>
                                    <FieldLabel hint={t('dashboards.builder.hints.size', '')}>
                                        {t('dashboards.builder.size', 'Widget size')}
                                    </FieldLabel>
                                    <SizeSelector value={size} onChange={setSize} />
                                </div>
                            </div>
                        </SectionCard>

                        {/* 3 · Data */}
                        {mode === 'single' ? (
                            <SectionCard step={3} title={t('dashboards.builder.section_data', 'Data & grouping')}>
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                    <div>
                                        <FieldLabel hint={t('dashboards.builder.hints.source', '')}>
                                            {t('dashboards.builder.source', 'Data source')}
                                        </FieldLabel>
                                        <Select value={source} onValueChange={changeSingleSource}>
                                            <SelectTrigger className="w-full">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {registry.sources.map((s) => (
                                                    <SelectItem key={s.key} value={s.key}>
                                                        <span className="flex items-center gap-2">
                                                            <Database className="text-muted-foreground h-4 w-4" />
                                                            {s.label}
                                                        </span>
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <FieldLabel hint={t('dashboards.builder.hints.measure', '')}>
                                            {t('dashboards.builder.measure', 'Measure')}
                                        </FieldLabel>
                                        <Select value={measure} onValueChange={changeSingleMeasure}>
                                            <SelectTrigger className="w-full">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {(sourceConfig?.measures ?? []).map((m) => (
                                                    <SelectItem key={m.key} value={m.key}>
                                                        {m.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <FieldLabel hint={t('dashboards.builder.hints.aggregation', '')}>
                                            {t('dashboards.builder.aggregation', 'Aggregation')}
                                        </FieldLabel>
                                        <Select
                                            value={aggregation}
                                            onValueChange={(v) => setAggregation(v as Aggregation)}
                                            disabled={(measureConfig?.aggregations.length ?? 0) <= 1}
                                        >
                                            <SelectTrigger className="w-full">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {(measureConfig?.aggregations ?? ['count']).map((a) => (
                                                    <SelectItem key={a} value={a}>
                                                        {aggregationLabel(a)}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    {chartType !== 'kpi' && (
                                        <div>
                                            <FieldLabel hint={t('dashboards.builder.hints.group_by', '')}>
                                                {t('dashboards.builder.dimension', 'Group by')}
                                            </FieldLabel>
                                            <Select value={dimension ?? ''} onValueChange={setDimension}>
                                                <SelectTrigger className="w-full">
                                                    <SelectValue placeholder={t('dashboards.builder.dimension_placeholder', 'Choose a dimension')} />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {(sourceConfig?.dimensions.length ?? 0) > 0 && (
                                                        <SelectGroup>
                                                            <SelectLabel>{t('dashboards.builder.by_category', 'By category')}</SelectLabel>
                                                            {(sourceConfig?.dimensions ?? []).map((dim) => (
                                                                <SelectItem key={dim.key} value={dim.key}>
                                                                    {dim.label}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectGroup>
                                                    )}
                                                    <SelectGroup>
                                                        <SelectLabel>{t('dashboards.builder.over_time', 'Over time')}</SelectLabel>
                                                        {(sourceConfig?.date_fields ?? []).map((field) => (
                                                            <SelectItem key={field.key} value={field.key}>
                                                                {field.label}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectGroup>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    )}
                                    <div className={chartType === 'kpi' ? '' : 'sm:col-start-2'}>
                                        <FieldLabel hint={t('dashboards.builder.hints.date_field', '')}>
                                            {t('dashboards.builder.date_field', 'Date filter field')}
                                        </FieldLabel>
                                        <Select value={dateField ?? NO_DATE} onValueChange={(v) => setDateField(v === NO_DATE ? null : v)}>
                                            <SelectTrigger className="w-full">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value={NO_DATE}>{t('dashboards.builder.all_time', 'All time')}</SelectItem>
                                                {(sourceConfig?.date_fields ?? []).map((field) => (
                                                    <SelectItem key={field.key} value={field.key}>
                                                        <span className="flex items-center gap-2">
                                                            <Calendar className="text-muted-foreground h-4 w-4" />
                                                            {field.label}
                                                        </span>
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            </SectionCard>
                        ) : (
                            <SectionCard step={3} title={t('dashboards.builder.section_series', 'Data & series')}>
                                <div className="mb-4 sm:max-w-[calc(50%-0.5rem)]">
                                    <FieldLabel hint={t('dashboards.builder.hints.axis', '')}>
                                        {t('dashboards.builder.axis', 'Compare across')}
                                    </FieldLabel>
                                    <Select value={axis} onValueChange={(v) => changeAxis(v as ComparisonAxis)}>
                                        <SelectTrigger className="w-full">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {availableAxes.map((a) => (
                                                <SelectItem key={a.value} value={a.value}>
                                                    {a.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="mb-2 flex items-center justify-between">
                                    <FieldLabel hint={t('dashboards.builder.hints.series', '')}>
                                        {t('dashboards.builder.series', 'Series')}
                                    </FieldLabel>
                                    <Button type="button" variant="outline" size="sm" onClick={addSeries} disabled={series.length >= 6}>
                                        <Plus className="mr-1 h-3 w-3" />
                                        {t('dashboards.builder.add_series', 'Add series')}
                                    </Button>
                                </div>

                                <div className="space-y-2">
                                    {series.map((serie, index) => {
                                        const serieSource = registry.sources.find((s) => s.key === serie.source);
                                        const serieMeasure = serieSource?.measures.find((m) => m.key === serie.measure);
                                        return (
                                            <div key={index} className="bg-muted/30 space-y-2 rounded-lg border p-3">
                                                <div className="flex items-center justify-between">
                                                    <span className="text-muted-foreground text-xs">
                                                        {t('dashboards.builder.series', 'Series')} {index + 1}
                                                    </span>
                                                    {series.length > 1 && (
                                                        <Button
                                                            type="button"
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-6 w-6"
                                                            onClick={() => removeSeries(index)}
                                                            aria-label={t('dashboards.builder.remove_series', 'Remove series')}
                                                        >
                                                            <X className="h-3.5 w-3.5" />
                                                        </Button>
                                                    )}
                                                </div>
                                                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                                    <Select value={serie.source} onValueChange={(v) => changeSeriesSource(index, v)}>
                                                        <SelectTrigger className="w-full">
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {allowedSources.map((s) => (
                                                                <SelectItem key={s.key} value={s.key}>
                                                                    {s.label}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                    <Select
                                                        value={serie.measure}
                                                        onValueChange={(v) =>
                                                            updateSeries(index, {
                                                                measure: v,
                                                                aggregation:
                                                                    serieSource?.measures.find((m) => m.key === v)?.aggregations[0] ?? 'count',
                                                            })
                                                        }
                                                    >
                                                        <SelectTrigger className="w-full">
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {(serieSource?.measures ?? []).map((m) => (
                                                                <SelectItem key={m.key} value={m.key}>
                                                                    {m.label}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                    {(serieMeasure?.aggregations.length ?? 0) > 1 && (
                                                        <Select
                                                            value={serie.aggregation}
                                                            onValueChange={(v) => updateSeries(index, { aggregation: v as Aggregation })}
                                                        >
                                                            <SelectTrigger className="w-full">
                                                                <SelectValue />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {(serieMeasure?.aggregations ?? ['count']).map((a) => (
                                                                    <SelectItem key={a} value={a}>
                                                                        {aggregationLabel(a)}
                                                                    </SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                    )}
                                                    {axis === 'time' && (
                                                        <Select
                                                            value={serie.date_field ?? ''}
                                                            onValueChange={(v) => updateSeries(index, { date_field: v })}
                                                        >
                                                            <SelectTrigger className="w-full">
                                                                <SelectValue placeholder={t('dashboards.builder.date_field', 'Date filter field')} />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {(serieSource?.date_fields ?? []).map((field) => (
                                                                    <SelectItem key={field.key} value={field.key}>
                                                                        {field.label}
                                                                    </SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </SectionCard>
                        )}

                        {/* 4 · Filters (single only) */}
                        {mode === 'single' && filterableDimensions.length > 0 && (
                            <SectionCard
                                step={4}
                                title={t('dashboards.builder.section_filters', 'Filters')}
                                action={
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="h-7 w-7"
                                        onClick={() => setFiltersOpen((v) => !v)}
                                        aria-label={t('dashboards.builder.section_filters', 'Filters')}
                                    >
                                        {filtersOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                    </Button>
                                }
                            >
                                {filtersOpen && (
                                    <div className="space-y-3">
                                        {filterableDimensions.map((dim) => (
                                            <div key={dim.key} className="space-y-1">
                                                <FieldLabel hint={t('dashboards.builder.hints.filters', '')}>{dim.label}</FieldLabel>
                                                <MultiSelect
                                                    options={dim.options}
                                                    selected={filterValues(dim.key)}
                                                    onChange={(values) => setFilter(dim.key, values)}
                                                    placeholder={t('dashboards.builder.filter_placeholder', 'All')}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </SectionCard>
                        )}

                        {/* 5 · Preview */}
                        <SectionCard
                            step={mode === 'single' && filterableDimensions.length > 0 ? 5 : 4}
                            title={t('dashboards.builder.section_preview', 'Preview')}
                            action={
                                <span className="text-muted-foreground flex items-center gap-1.5 text-xs">
                                    <span className={cn('h-2 w-2 rounded-full', preview.result ? 'bg-success' : 'bg-muted-foreground/40')} />
                                    {t('dashboards.builder.live_preview', 'Live preview')}
                                </span>
                            }
                        >
                            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                                <div className="lg:col-span-2">
                                    {preview.result && preview.result.kind !== 'error' ? (
                                        <div className="space-y-2">
                                            {/* Same chrome as the real dashboard card (WidgetCardShell), so
                                                this is a true preview — not an approximation. */}
                                            <WidgetCardShell title={previewTitle}>
                                                <WidgetChart result={preview.result} />
                                            </WidgetCardShell>
                                            <p className="text-muted-foreground px-1 text-xs">
                                                {previewSubtitle} · {previewFooter}
                                            </p>
                                        </div>
                                    ) : preview.error ? (
                                        <div className="border-input text-danger-subtle-foreground flex min-h-[140px] items-center justify-center rounded-lg border py-10 text-center text-sm">
                                            {t('dashboards.builder.preview_error', 'Preview failed. Check the widget settings.')}
                                        </div>
                                    ) : (
                                        <div className="border-input text-muted-foreground flex min-h-[140px] items-center justify-center gap-2 rounded-lg border py-10 text-center text-sm">
                                            {preview.loading && <Loader2 className="h-4 w-4 animate-spin" />}
                                            {t('dashboards.builder.preview_empty', 'Finish configuring the widget to see a preview.')}
                                        </div>
                                    )}
                                </div>
                                <div className="bg-info-subtle/40 rounded-lg p-3 text-xs">
                                    <div className="text-foreground mb-1 flex items-center gap-1.5 font-medium">
                                        <Info className="text-info-subtle-foreground h-3.5 w-3.5" />
                                        {t('dashboards.builder.preview_note_title', 'About this preview')}
                                    </div>
                                    <p className="text-muted-foreground">
                                        {t(
                                            'dashboards.builder.preview_note_body',
                                            'Runs against your live data for the current dashboard date range.',
                                        )}
                                    </p>
                                </div>
                            </div>
                        </SectionCard>
                    </div>
                </TooltipProvider>

                <ResponsiveModalFooter className="border-t p-4">
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        {t('common.cancel', 'Cancel')}
                    </Button>
                    <Button onClick={handleSave} disabled={!preview.result}>
                        {initialWidget
                            ? t('dashboards.builder.update_widget', 'Update widget')
                            : t('dashboards.builder.add_to_dashboard', 'Add widget')}
                    </Button>
                </ResponsiveModalFooter>
            </ResponsiveModalContent>
        </ResponsiveModal>
    );
}

function SectionCard({ step, title, action, children }: { step: number; title: string; action?: ReactNode; children: ReactNode }) {
    return (
        <div className="rounded-xl border p-4 sm:p-5">
            <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <span className="border-primary/40 text-primary flex h-5 w-5 items-center justify-center rounded-full border text-xs font-semibold">
                        {step}
                    </span>
                    <h3 className="text-sm font-semibold">{title}</h3>
                </div>
                {action}
            </div>
            {children}
        </div>
    );
}

function FieldLabel({ children, hint }: { children: ReactNode; hint?: string }) {
    return (
        <div className="mb-1.5 flex items-center gap-1">
            <Label>{children}</Label>
            {hint ? (
                <Tooltip>
                    <TooltipTrigger asChild>
                        <span className="text-muted-foreground/50 hover:text-muted-foreground cursor-help">
                            <Info className="h-3.5 w-3.5" />
                        </span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">{hint}</TooltipContent>
                </Tooltip>
            ) : null}
        </div>
    );
}

function ChartTypeLabel({ type, label }: { type: ChartType; label: string }) {
    const Icon = CHART_ICONS[type];
    return (
        <span className="flex items-center gap-2">
            <Icon className="text-muted-foreground h-4 w-4" />
            {label}
        </span>
    );
}

const SIZE_OPTIONS: Array<{ value: WidgetSize; letter: string }> = [
    { value: 'small', letter: 'S' },
    { value: 'medium', letter: 'M' },
    { value: 'full', letter: 'L' },
];

function SizeSelector({ value, onChange }: { value: WidgetSize; onChange: (value: WidgetSize) => void }) {
    const { t } = useTranslations();
    const labels: Record<WidgetSize, string> = {
        small: t('dashboards.builder.sizes.small', 'Small'),
        medium: t('dashboards.builder.sizes.medium', 'Medium'),
        full: t('dashboards.builder.sizes.full', 'Large'),
    };

    return (
        <div className="grid grid-cols-3 gap-2">
            {SIZE_OPTIONS.map((option) => (
                <Button
                    key={option.value}
                    type="button"
                    variant="outline"
                    onClick={() => onChange(option.value)}
                    className={cn('h-auto flex-col gap-0.5 py-2', value === option.value && 'border-primary ring-primary/20 bg-accent ring-2')}
                >
                    <span className="text-sm font-semibold">{option.letter}</span>
                    <span className="text-muted-foreground text-xs font-normal">{labels[option.value]}</span>
                </Button>
            ))}
        </div>
    );
}
